import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  REVIEWABLE_DOCUMENT_TYPES,
  ReviewableDocumentInput,
  reviewableDocumentInputSchema,
} from '@bordrio/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import _ from 'lodash';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CheckedInput, SelectInput, TextInput } from '@src/components';
import { useUpdateNifOrderMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import ReviewableDocumentFileInput from '../../components/ReviewableDocumentFileInput/ReviewableDocumentFileInput';
import { extractReviewableDocumentInputFromNifOrder } from '../../utils/reviewable-document';
import { VALIDATIONS } from '../../utils/validation';
import { NifOrderStepPageProps } from '../types/stepProps';

// t('nif:validation.document_number', 'Enter the document number')

const formSchema = z.object({
  parents: z
    .array(
      z.object({
        id: z.string().optional(),
        position: z.number(),
        givenNames: VALIDATIONS.requiredStringWithoutSpecialChars,
        surname: VALIDATIONS.requiredStringWithoutSpecialChars,
        documentNumber: z.string().min(1, 'nif:validation.document_number'),
        proofOfIdentity: reviewableDocumentInputSchema,
      }),
    )
    .min(1)
    .max(2),
});

type FormData = z.infer<typeof formSchema>;

interface ParentFormProps {
  control: Control<FormData>;
  index: number;
}

function ParentForm({ control, index }: ParentFormProps): JSX.Element {
  const { t } = useTranslation(['nif']);

  const documentTypeOptions: {
    label: string;
    value: string;
  }[] = [
    {
      label: t('document_types.passport', 'Passport', { ns: 'translation' }),
      value: REVIEWABLE_DOCUMENT_TYPES.passport.id,
    },
    {
      label: t('document_types.eu_id', 'EU National ID card', {
        ns: 'translation',
      }),
      value: REVIEWABLE_DOCUMENT_TYPES.eu_id_card.id,
    },
  ];

  return (
    <div className="space-y-4">
      <h3>
        <Trans
          t={t}
          i18nKey="parents_information.parent_form.title"
          defaults="Parent {{n}}"
          values={{ n: index + 1 }}
        />
      </h3>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <TextInput.LabelledController
          label={t(
            'parents_information.parent_form.given_names_label',
            'Given name(s)',
          )}
          control={control}
          name={`parents.${index}.givenNames`}
        />
        <TextInput.LabelledController
          label={t('parents_information.parent_form.surname_label', 'Surname')}
          control={control}
          name={`parents.${index}.surname`}
        />
        <SelectInput.LabelledController
          label={t(
            'parents_information.parent_form.document_type_label',
            'Document type',
          )}
          options={documentTypeOptions}
          control={control}
          name={`parents.${index}.proofOfIdentity.currentVersion.documentTypeId`}
        />
        <TextInput.LabelledController
          label={t(
            'parents_information.parent_form.doc_number_label',
            'Document number',
          )}
          control={control}
          name={`parents.${index}.documentNumber`}
        />
        <ReviewableDocumentFileInput
          control={control}
          name={`parents.${index}.proofOfIdentity`}
          horizontalLayout
          className="col-span-2"
        />
      </div>
    </div>
  );
}

function NifParentsInformationPage({
  nifOrder,
  previousUrl,
  navigateToNextStep,
}: NifOrderStepPageProps): JSX.Element {
  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: nifOrder.parents.length
      ? {
          parents: _.sortBy(
            nifOrder.parents.map((p) => ({
              ..._.pick(p, [
                'id',
                'position',
                'givenNames',
                'surname',
                'documentNumber',
              ]),
              proofOfIdentity: extractReviewableDocumentInputFromNifOrder(
                nifOrder,
                'nif_parent_proof_of_identity',
                'passport',
                p.id,
              ),
            })),
            (p) => p.position,
          ),
        }
      : {
          parents: [
            {
              position: 1,
              proofOfIdentity: {
                currentVersion: {
                  documentTypeId: REVIEWABLE_DOCUMENT_TYPES.passport.id,
                },
              },
            },
            {
              position: 2,
              proofOfIdentity: {
                currentVersion: {
                  documentTypeId: REVIEWABLE_DOCUMENT_TYPES.passport.id,
                },
              },
            },
          ],
        },
    resolver: zodResolver(formSchema),
  });
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const toast = useToast();
  const { t } = useTranslation(['nif']);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parents',
  });

  function onSubmit(formData: FormData): void {
    updateNifOrder({
      variables: {
        input: {
          id: nifOrder.id,
          data: {
            parents: formData.parents?.map(({ proofOfIdentity, ...rest }) => ({
              ...rest,
              intakeDocuments: [
                {
                  documentCategoryId:
                    REVIEWABLE_DOCUMENT_CATEGORIES.nif_parent_proof_of_identity
                      .id,
                  reviewableDocument: proofOfIdentity,
                },
              ],
            })),
          },
        },
      },
    })
      .then((updatedOrder) => {
        navigateToNextStep(updatedOrder?.data?.updateNifOrder?.nifOrder);
      })
      .catch((err) => toast.error(logAndFormatError(err)));
  }

  const handleSingleParentChange = (singleParent: boolean): void => {
    if (singleParent) {
      remove(1);
    } else {
      append({
        position: 2,
        proofOfIdentity: {
          currentVersion: {
            documentTypeId: REVIEWABLE_DOCUMENT_TYPES.passport.id,
          },
        } as ReviewableDocumentInput,
        givenNames: '',
        surname: '',
        documentNumber: '',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>{t('parents_information.title', "Parents' information")}</h2>
        <p>
          {t(
            'parents_information.body',
            'This information will be used to generate the Power of Attorney for the minor.',
          )}
        </p>
      </div>
      <ParentForm control={control} index={0} />
      <CheckedInput.Labelled
        label={t(
          'parents_information.checkbox_single_parent',
          'Applicant is represented by a single parent',
        )}
        checked={fields.length === 1}
        onChange={handleSingleParentChange}
      />
      {fields[1] && <ParentForm control={control} index={1} />}

      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default NifParentsInformationPage;
